import type {TimeEntry} from '@package/shared'
import {useCallback} from 'react'
import {EditModeButtons} from '../../components/EditModeButtons.tsx'
import type {DateRange} from '../../../../components/dateRangeSelector/DateRangeSelector.tsx'
import type {AddTimeEntryRequest} from '@app/api-resolver/router'
import {TimeEntryFormFields} from '../TimeEntryFormFields.tsx'
import {useForm} from '@mantine/form'
import type {PartialTimeEntryFormValues} from '../PartialTimeEntryFormValues.ts'
import {TimeEntryFormValues} from '../TimeEntryFormValues.ts'
import {zod4Resolver} from 'mantine-form-zod-resolver'

type Props = {
  day: DateRange
  entry: Partial<TimeEntry>
  onAdd: (request: AddTimeEntryRequest) => Promise<void>
  adding: boolean
  onCancel: () => Promise<void>
  disabled: boolean
}

export const AddTimeEntryForm = ({day, entry, onAdd, adding, onCancel, disabled: containerDisabled}: Props) => {
  const form = useForm<PartialTimeEntryFormValues, TimeEntryFormValues | undefined>({
    initialValues: {
      start: entry.start || '',
      end: entry.end || '',
      description: entry.description,
      breakHours: entry.breakHours || 0,
      projectId: entry.projectId || null,
    },
    validate: zod4Resolver(TimeEntryFormValues),
    transformValues: (values) => TimeEntryFormValues.safeParse(values).data,
  })

  const handleSubmit = useCallback(
    async (formValues: TimeEntryFormValues | undefined) => {
      if (!formValues) return
      await onAdd(formValues satisfies AddTimeEntryRequest)
    },
    [onAdd],
  )

  const disabled = containerDisabled

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} style={{display: 'flex', flexDirection: 'column', gap: 5}}>
      <TimeEntryFormFields form={form} day={day} disabled={disabled} />

      <EditModeButtons
        submitVariant={'add'}
        destructiveVariant={'cancel'}
        submitting={adding}
        onDestruct={onCancel}
        disabled={disabled}
      />
    </form>
  )
}
